import { BookingLink } from "./BookingLink.tsx";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./site-chrome.css";

/** Split either side of the crest: the first two on the left, the rest right. */
const SECTIONS = [
  { id: "treatments", label: "Treatments" },
  { id: "studio", label: "The Studio" },
  { id: "about", label: "About" },
  { id: "visit", label: "Visit" },
];

const START = SECTIONS.slice(0, 2);
const END = SECTIONS.slice(2);

interface SiteHeaderProps {
  /** The one-pager gets scroll-spy nav; inner pages get a plain back link. */
  variant?: "home" | "inner";
}

export function SiteHeader({ variant = "home" }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll spy. Only meaningful on the one-pager.
  useEffect(() => {
    if (variant !== "home" || typeof IntersectionObserver === "undefined")
      return;

    const observer = new IntersectionObserver(
      (entries) => {
        // The section closest to the top of the viewport wins.
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-25% 0px -60% 0px" },
    );

    for (const section of SECTIONS) {
      const element = document.getElementById(section.id);
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, [variant]);

  // Lock the page behind the mobile menu.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  /*
   * Over the homepage photograph the header is transparent, with the crest at
   * full size. Once the page moves it settles into a compact solid bar. Inner
   * pages have no photograph to sit over, so they start settled; so does an
   * open menu, whose panel is positioned against the compact bar's height.
   */
  const settled = variant === "inner" || scrolled || menuOpen;

  const link = (section: (typeof SECTIONS)[number]) => (
    <a
      key={section.id}
      href={`#${section.id}`}
      className={`site-nav__link ${active === section.id ? "site-nav__link--active" : ""}`}
    >
      {section.label}
    </a>
  );

  return (
    <>
      <header
        className={`site-header ${settled ? "site-header--settled" : ""}`}
      >
        <nav className="page site-header__inner" aria-label="Main">
          <div className="site-header__start">
            {variant === "home" ? (
              <>
                <div className="site-nav">{START.map(link)}</div>
                <BookingLink className="site-nav__link site-header__book">
                  Book
                </BookingLink>
              </>
            ) : (
              <Link to="/" className="site-nav__link">
                <span aria-hidden="true">&larr;</span>
                <span className="site-header__back-full">Back to the studio</span>
                <span className="site-header__back-short">Back</span>
              </Link>
            )}
          </div>

          <Link
            to="/"
            className="site-header__crest"
            aria-label="The Renewal Studio, home"
            onClick={() => setMenuOpen(false)}
          >
            <img src="/images/logo.webp" alt="" width={112} height={111} />
          </Link>

          <div className="site-header__end">
            {variant === "home" && (
              <>
                <div className="site-nav">
                  {END.map(link)}
                  <BookingLink className="btn btn--ghost site-header__cta">
                    Book
                  </BookingLink>
                </div>
                <button
                  type="button"
                  className="menu-toggle"
                  aria-expanded={menuOpen}
                  aria-controls="mobile-menu"
                  aria-label={menuOpen ? "Close menu" : "Open menu"}
                  onClick={() => setMenuOpen((open) => !open)}
                >
                  <span
                    className={`menu-toggle__bar ${menuOpen ? "is-open-top" : ""}`}
                  />
                  <span
                    className={`menu-toggle__bar ${menuOpen ? "is-open-bottom" : ""}`}
                  />
                </button>
              </>
            )}
          </div>
        </nav>
      </header>

      {/*
        Deliberately a sibling of <header>, not a child.

        The settled header carries backdrop-filter, and that establishes a
        containing block for position: fixed descendants. Nested inside it, the
        panel's `inset: <header-height> 0 0` resolved against the 72px-tall
        header rather than the viewport - so it collapsed to a 96px strip, its
        background covered almost nothing, and the links overflowed straight
        over the hero.
      */}
      {variant === "home" && (
        <div
          id="mobile-menu"
          className={`mobile-menu ${menuOpen ? "mobile-menu--open" : ""}`}
          hidden={!menuOpen}
        >
          <nav aria-label="Sections">
            {SECTIONS.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                onClick={() => setMenuOpen(false)}
              >
                {section.label}
              </a>
            ))}
            <BookingLink onClick={() => setMenuOpen(false)}>
              Book an appointment
            </BookingLink>
          </nav>
        </div>
      )}
    </>
  );
}
